import { SYSTEMS, detectSystem } from '../../../../src/client/cores/systems.js';
import { unzipSync } from 'fflate';

/**
 * Pure logic for the library's ROM-upload dialog: extension classification,
 * bounded client-side zip unwrapping (the inner cartridge bytes are what
 * gets hashed/uploaded, matching how gen-rom-index hashes bundled zips),
 * and per-file upload preparation with size preflight.
 *
 * cores/systems.js is a dependency-free data module, so importing it here
 * does not drag main-app runtime code into the library bundle.
 */

export const MAX_UPLOAD_BYTES = 32 * 1024 * 1024;   // mirror server MAX_ROM_SIZE
export const MAX_ZIP_FILE_BYTES = 40 * 1024 * 1024; // compressed preflight cap
// Bomb guard for unzipSync: bound what a hostile archive can make us
// decompress. Bounded synchronous extraction (worst case ~64 MB) — a worker
// would add machinery without adding a bound.
export const ZIP_CAPS = Object.freeze({
  maxEntries: 512,
  maxEntryBytes: MAX_UPLOAD_BYTES,
  maxTotalBytes: 64 * 1024 * 1024,
});

/** Distinct system ids whose extension list includes `ext`. */
export function systemsForExtension(ext) {
  // 32X shares Genesis's catalog system, but the qualified replay registry
  // cannot gain a new extension just for this separate upload bundle.
  if (ext === '.32x') return ['genesis'];
  const ids = [];
  for (const sys of Object.values(SYSTEMS)) {
    if (sys.extensions.includes(ext) && !ids.includes(sys.id)) ids.push(sys.id);
  }
  return ids;
}

/** accept= attribute for the file input: every system's extensions + .zip. */
export function acceptExtensions() {
  const exts = new Set(['.zip', '.gbc', '.32x']); // .gbc maps to gb; 32X maps to genesis
  for (const sys of Object.values(SYSTEMS)) {
    for (const e of sys.extensions) exts.add(e);
  }
  return [...exts].join(',');
}

/**
 * Classify one (post-zip-unwrap) filename.
 *   { ok: true, system, ambiguous: false }        — unambiguous
 *   { ok: true, ambiguous: true, options: [ids] } — e.g. .bin (Atari/Genesis)
 *   { ok: false, reason: 'unsupported' }
 */
export function classifyRomFile(filename) {
  const dot = filename.lastIndexOf('.');
  const ext = dot >= 0 ? filename.slice(dot).toLowerCase() : '';
  const detected = detectSystem(filename); // null for ambiguous/unknown
  if (detected) return { ok: true, system: detected.id, ambiguous: false, ext };
  if (ext === '.zip') return { ok: false, reason: 'unsupported', ext }; // unwrap first
  const options = systemsForExtension(ext);
  if (options.length > 1) return { ok: true, ambiguous: true, options, ext };
  if (options.length === 1) return { ok: true, system: options[0], ambiguous: false, ext };
  return { ok: false, reason: 'unsupported', ext };
}

/**
 * Entry filter for unzipSync — the zip-bomb guard. Accepts only
 * stored/deflate entries within the per-entry, running-total, and count
 * caps; everything else is skipped (never decompressed).
 */
export function makeZipEntryFilter(caps = ZIP_CAPS) {
  let count = 0;
  let total = 0;
  return (info) => {
    if (info.compression !== 0 && info.compression !== 8) return false;
    if (count >= caps.maxEntries) return false;
    if (info.originalSize > caps.maxEntryBytes) return false;
    if (total + info.originalSize > caps.maxTotalBytes) return false;
    count += 1;
    total += info.originalSize;
    return true;
  };
}

/**
 * Unwrap a .zip under ZIP_CAPS. Prefers an entry classifyRomFile
 * recognizes; falls back to the largest accepted entry (v1
 * `_extractRomFromZip` parity). Returns { name, data } or null.
 */
export function extractRomFromZip(zipBytes, caps = ZIP_CAPS) {
  let files;
  try { files = unzipSync(zipBytes, { filter: makeZipEntryFilter(caps) }); }
  catch { return null; }
  const entries = Object.entries(files).filter(([, d]) => d && d.length > 0);
  if (!entries.length) return null;
  for (const [name, data] of entries) {
    const cls = classifyRomFile(name);
    if (cls.ok) return { name, data };
  }
  let largest = entries[0];
  for (const e of entries) if (e[1].length > largest[1].length) largest = e;
  return { name: largest[0], data: largest[1] };
}

/**
 * Read + prepare one picked DOM File for upload. Size preflight runs on
 * file.size BEFORE arrayBuffer so an oversized pick never gets read into
 * memory.
 * → { file, name, bytes, system }                    ready
 * → { file, name, bytes, ambiguous: true, options }  needs a system pick
 * → { file, name, error: 'unsupported'|'no_rom_in_zip'|'too_large' }
 */
export async function prepareUpload(file) {
  const isZip = file.name.toLowerCase().endsWith('.zip');
  if (file.size > (isZip ? MAX_ZIP_FILE_BYTES : MAX_UPLOAD_BYTES)) {
    return { file, name: file.name, error: 'too_large' };
  }
  const buf = new Uint8Array(await file.arrayBuffer());
  let name = file.name;
  let bytes = buf;
  if (isZip) {
    const inner = extractRomFromZip(buf);
    if (!inner) return { file, name, error: 'no_rom_in_zip' };
    name = inner.name;
    bytes = inner.data;
  }
  if (bytes.length > MAX_UPLOAD_BYTES) return { file, name, error: 'too_large' };
  const cls = classifyRomFile(name);
  if (!cls.ok) return { file, name, error: 'unsupported' };
  if (cls.ambiguous) return { file, name, bytes, ambiguous: true, options: cls.options };
  return { file, name, bytes, system: cls.system };
}
